'use client';

import { useCallback, useEffect, useState } from 'react';
import type { DiscoveredDevice } from '../types';

interface HidDevice {
  productName: string;
  vendorId: number;
  productId: number;
}

interface NavigatorWithHid extends Navigator {
  hid?: {
    requestDevice(options: {
      filters: Array<Record<string, number>>;
    }): Promise<HidDevice[]>;
    getDevices(): Promise<HidDevice[]>;
  };
}

export type BarcodeDiscoveryStatus =
  | 'idle'
  | 'unsupported'
  | 'loading'
  | 'ready'
  | 'permission-denied'
  | 'error';

export interface UseBarcodeDiscoveryResult {
  status: BarcodeDiscoveryStatus;
  devices: DiscoveredDevice[];
  error: string | null;
  pickDevice: () => Promise<void>;
  reset: () => void;
}

function toDiscovered(d: HidDevice): DiscoveredDevice {
  const vendor = d.vendorId.toString(16).padStart(4, '0');
  const product = d.productId.toString(16).padStart(4, '0');
  return {
    device_type: 'barcode_scanner',
    name: d.productName || `HID ${vendor}:${product}`,
    scanner_identifier: null,
    serial_number: `${vendor}:${product}`,
    ip_address: null,
    software_version: null
  };
}

export function useBarcodeDiscovery(
  enabled: boolean
): UseBarcodeDiscoveryResult {
  const [status, setStatus] = useState<BarcodeDiscoveryStatus>('idle');
  const [devices, setDevices] = useState<DiscoveredDevice[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === 'undefined') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- WebHID feature detection happens after mount
      setStatus('unsupported');
      return;
    }
    const hid = (navigator as NavigatorWithHid).hid;
    if (!hid) {
      setStatus('unsupported');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const granted = await hid.getDevices();
        if (cancelled) return;
        if (granted.length > 0) {
          setDevices(granted.map(toDiscovered));
          setStatus('ready');
        } else {
          setStatus('idle');
        }
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setError(err instanceof Error ? err.message : String(err));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const pickDevice = useCallback(async () => {
    if (typeof navigator === 'undefined') return;
    const hid = (navigator as NavigatorWithHid).hid;
    if (!hid) {
      setStatus('unsupported');
      return;
    }
    try {
      setStatus('loading');
      setError(null);
      const picked = await hid.requestDevice({ filters: [] });
      if (picked.length === 0) {
        setStatus('permission-denied');
        return;
      }
      setDevices((prev) => {
        const seen = new Set(prev.map((d) => d.serial_number));
        const additions = picked
          .map(toDiscovered)
          .filter((d) => !seen.has(d.serial_number));
        return [...prev, ...additions];
      });
      setStatus('ready');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const reset = useCallback(() => {
    setDevices([]);
    setStatus('idle');
    setError(null);
  }, []);

  return { status, devices, error, pickDevice, reset };
}
